import React from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  IconButton,
  Chip,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { QuestionnairePopup } from '../../types/questionnaire.types';

interface PopupListProps {
  popups: QuestionnairePopup[];
  onAdd: () => void;
  onEdit: (popup: QuestionnairePopup) => void;
  onDelete: (popupId: string) => void;
}

export const PopupList: React.FC<PopupListProps> = ({ popups, onAdd, onEdit, onDelete }) => {
  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const stripHtmlTags = (html: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Popups</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAdd}
        >
          Add Popup
        </Button>
      </Box>

      {popups.length === 0 ? (
        <Alert severity="info">
          No popups yet. Create a popup to display custom dialogs in your questionnaire.
        </Alert>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
          {popups.map((popup) => (
            <Card
              key={popup.id}
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                '&:hover': {
                  boxShadow: 3,
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, flex: 1 }}>
                  {popup.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => onEdit(popup)}
                    title="Edit popup"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      if (window.confirm(`Are you sure you want to delete the popup "${popup.title}"?`)) {
                        onDelete(popup.id);
                      }
                    }}
                    title="Delete popup"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {popup.content && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  {truncateText(stripHtmlTags(popup.content), 100)}
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                <Chip
                  label={`${popup.buttons.length} button${popup.buttons.length !== 1 ? 's' : ''}`}
                  size="small"
                  variant="outlined"
                />
                {popup.buttons.map((button) => (
                  <Chip
                    key={button.id}
                    label={button.label}
                    size="small"
                    color={button.buttonStyle === 'primary' ? 'primary' : 'default'}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};
